/**
 * `/ask-paygbot` — full-page wrapper around the existing
 * `AskPayGBot` component so deep links from task-surface helper
 * chips (e.g. `DailyMissionCard`'s compact "Ask PayGBot" chip and
 * the pre-existing `AskPayGBotHelperButton`) resolve to a real
 * route instead of being caught by the `*` fallback.
 *
 * Round 101 (2026-07-23) — universal completion routing.
 * The chip serialises the full mission context (task_id,
 * task_label, task_description, mission_id, asset_id, asset_type,
 * business_id, business_name, niche, target_audience,
 * preferred_platform, publishing_history, niche_context,
 * asset_payload_summary) into a base64-JSON `wctx` query param.
 * We decode it here and pass it through to `AskPayGBot` as
 * `initialMessage` (a comprehensive framed prompt) so the
 * assistant answers with FULL awareness of what the customer is
 * trying to accomplish — no round-trip "what's your niche?".
 *
 * IMPORTANT — no visual/layout change to `AskPayGBot`,
 * `DailyMissionCard`, `ExecutionPanel`, or `DashboardPage`.
 */
import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from '@phosphor-icons/react';
import { getUser } from '@/utils/auth';
import AskPayGBot from '@/components/AskPayGBot';

const decodeCtx = (raw) => {
  if (!raw) return null;
  try {
    const json = decodeURIComponent(escape(window.atob(raw)));
    const parsed = JSON.parse(json);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  } catch {
    return null;
  }
};

const composeInitialMessage = (rawPrompt, ctx) => {
  const prompt = (rawPrompt || '').trim();
  if (!ctx) return prompt;

  // Compact framed prompt — every line optional so unknown fields
  // don't leak `undefined`. The framing is short so the actual
  // question stays at the top of the composer.
  const lines = [];
  if (prompt) lines.push(prompt);
  const bits = [];
  if (ctx.business_name) bits.push(`Business: ${ctx.business_name}`);
  if (ctx.niche) bits.push(`Niche: ${ctx.niche}`);
  if (ctx.target_audience) bits.push(`Audience: ${ctx.target_audience}`);
  if (ctx.preferred_platform) bits.push(`Platform: ${ctx.preferred_platform}`);
  if (bits.length) {
    lines.push('');
    lines.push(`[Context — ${bits.join(' · ')}]`);
  }
  const missionBits = [];
  if (ctx.mission_title) missionBits.push(`Mission: ${ctx.mission_title}`);
  if (ctx.asset_type) missionBits.push(`Type: ${ctx.asset_type}`);
  if (ctx.task_id) missionBits.push(`Task: ${ctx.task_id}`);
  if (missionBits.length) {
    lines.push(`[${missionBits.join(' · ')}]`);
  }
  const publishing = ctx.publishing_history || {};
  if (typeof publishing.current_streak === 'number'
      || typeof publishing.posted_today === 'boolean') {
    lines.push(
      `[Streak: ${publishing.current_streak || 0} · posted today: `
      + `${publishing.posted_today ? 'yes' : 'no'}]`,
    );
  }
  if (ctx.publishing_instructions) {
    lines.push(`[Publishing steps — ${ctx.publishing_instructions}]`);
  }
  return lines.join('\n').trim();
};

export default function AskPayGBotPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [user] = useState(() => getUser());

  const rawPrompt = useMemo(
    () => (searchParams.get('prompt') || '').trim(),
    [searchParams],
  );
  const ctx = useMemo(
    () => decodeCtx(searchParams.get('wctx')),
    [searchParams],
  );
  const initialMessage = useMemo(
    () => composeInitialMessage(rawPrompt, ctx),
    [rawPrompt, ctx],
  );

  return (
    <div
      className="min-h-[100dvh] h-[100dvh] bg-slate-950 text-white flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="ask-paygbot-page"
    >
      <div
        className="max-w-3xl w-full mx-auto flex flex-col flex-1 min-h-0 px-3 sm:px-6 pt-2 pb-2 sm:py-6"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)' }}
      >
        <motion.button
          type="button"
          onClick={() => navigate('/dashboard')}
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -2 }}
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-1 sm:mb-6 min-h-[36px] py-1 sm:py-2 -mx-1 px-1 flex-shrink-0"
          data-testid="ask-paygbot-back-btn"
        >
          <ArrowLeft weight="bold" size={16} />
          <span>Back to Dashboard</span>
        </motion.button>

        {/* Round 106+107 — on mobile, collapse the page title +
            subtitle + prompt-hint into a single compact strip so
            the actual conversation gets the majority of the
            viewport. Desktop keeps the fuller header. */}
        <h1
          className="text-base sm:text-2xl font-semibold mb-0.5 sm:mb-1 break-words flex-shrink-0"
          data-testid="ask-paygbot-page-title"
        >
          Ask PayGBot
        </h1>
        {rawPrompt ? (
          <p
            className="hidden sm:block text-sm text-slate-400 mb-3 sm:mb-4 break-words"
            data-testid="ask-paygbot-page-prompt-hint"
          >
            Answering:{' '}
            <span className="text-slate-200 font-medium">{rawPrompt}</span>
          </p>
        ) : (
          <p className="hidden sm:block text-sm text-slate-400 mb-3 sm:mb-4">
            Type a question below — PayGBot will help you move forward.
          </p>
        )}
        {ctx && (ctx.business_name || ctx.niche) && (
          <p
            className="text-[11px] text-slate-500 mb-1 sm:mb-4 break-words truncate flex-shrink-0"
            data-testid="ask-paygbot-page-context"
          >
            {ctx.business_name ? `${ctx.business_name} · ` : ''}
            {ctx.niche ? `${ctx.niche} · ` : ''}
            {ctx.preferred_platform || ''}
          </p>
        )}

        <AskPayGBot
          user={user}
          defaultExpanded
          initialMessage={initialMessage}
          className="bg-slate-900/60 border border-white/10 rounded-2xl p-2 sm:p-4 overflow-hidden flex-1 min-h-0"
        />
      </div>
    </div>
  );
}
